import { LANGUAGES, languageLessons, type Language, type Lesson } from '@typing-racer/shared';
import { clear, h, pct } from '../dom.js';
import type { LessonRecord } from '../lesson.js';

const stars = (n: number) => '★'.repeat(n) + '☆'.repeat(3 - n);

export function renderLanguages(
  root: HTMLElement,
  progress: Record<string, LessonRecord>,
  selected: Language | null,
  actions: { select(l: Language | null): void; start(lesson: Lesson): void; back(): void },
): void {
  clear(root);
  const body: HTMLElement[] = [];
  if (!selected) {
    body.push(
      h('p', { class: 'muted' }, 'Pick a language. You type each phrase in that language and its meaning is shown underneath as you go. Accented letters can be typed without the accent.'),
      h(
        'div',
        { class: 'languages' },
        LANGUAGES.map((lang) => {
          const lessons = languageLessons(lang);
          const earned = lessons.reduce((n, l) => n + (progress[l.id]?.stars ?? 0), 0);
          return h(
            'button',
            { class: 'lang-card', onclick: () => actions.select(lang) },
            h('span', { class: 'flag' }, lang.flag),
            h('b', {}, lang.name),
            h('span', { class: 'muted small' }, `${lang.sets.length} sets · ${lang.sets.reduce((n, s) => n + s.phrases.length, 0)} phrases`),
            h('span', { class: `stars${earned ? '' : ' none'}` }, `${earned}/${lessons.length * 3} ★`),
          );
        }),
      ),
    );
  } else {
    const lessons = languageLessons(selected);
    body.push(
      h('div', { class: 'row' }, h('button', { class: 'btn small ghost', onclick: () => actions.select(null) }, '← All languages'), h('h2', { style: 'margin:0' }, `${selected.flag} ${selected.name}`)),
      h(
        'div',
        { class: 'lessons' },
        lessons.map((l, i) => {
          const rec = progress[l.id];
          const set = selected.sets[i]!;
          return h(
            'div',
            { class: 'lesson-card' },
            h('div', { class: 'lesson-num' }, String(i + 1)),
            h(
              'div',
              { class: 'lesson-body' },
              h('b', {}, set.title),
              h('div', { class: 'muted small' }, set.phrases.slice(0, 3).map((p) => p.text).join(' · ') + ' …'),
            ),
            h(
              'div',
              { class: 'lesson-side' },
              h('div', { class: `stars${rec ? '' : ' none'}` }, stars(rec?.stars ?? 0)),
              rec ? h('div', { class: 'muted small' }, `Best ${Math.round(rec.bestWpm)} WPM, ${pct(rec.bestAccuracy)}`) : h('div', { class: 'muted small' }, 'Not started'),
              h('button', { class: 'btn small primary', onclick: () => actions.start(l) }, rec ? 'Practise again' : 'Start'),
            ),
          );
        }),
      ),
    );
  }
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel wide stack' },
        h('div', { class: 'row', style: 'justify-content:space-between' }, h('h1', {}, '🌍 Learn a language'), h('button', { class: 'btn small ghost', onclick: actions.back }, '← Back')),
        body,
      ),
    ),
  );
}
